/**
 * Aviado canonical goal universe (v2.7)
 *
 * Derived from lib/aviado/aviado_bundle.json (synced from the @aviado/bundle
 * workspace package via `npm run bundle:sync`). If you need to change goal
 * metadata, edit the bundle in that monorepo, then re-sync — do not edit
 * the editorial descriptions or slug map here unless you intend to diverge.
 *
 * Research Library editorial notes:
 * - Labels, icons, and Tier A/B come straight from the bundle.
 * - `editorialDescription` is a reader-facing rewrite for the library — warmer
 *   tone than the in-app `description`. If missing, UI falls back to `description`.
 * - `athletic` and `stress` are NOT goals here — they live in `topics[]` on
 *   articles (see lib/types.ts `Topic`).
 */
import bundle from './aviado_bundle.json';

export type GoalTier = 'A' | 'B';

export type GoalSlug =
  | 'metabolic'
  | 'cardiovascular'
  | 'brain-cognitive'
  | 'sleep'
  | 'hormonal'
  | 'muscle-bone-joint'
  | 'immune'
  | 'longevity'
  | 'gut'
  | 'skin-hair';

/** Canonical bundle label for a slug (used when querying the bundle JSON). */
export const GOAL_CANONICAL_LABEL: Record<GoalSlug, string> = {
  metabolic: 'Metabolic Health',
  cardiovascular: 'Cardiovascular & Circulation',
  'brain-cognitive': 'Brain, Mood & Cognitive Performance',
  sleep: 'Sleep & Recovery',
  hormonal: 'Hormonal Health',
  'muscle-bone-joint': 'Muscle, Bone & Joint',
  immune: 'Immune System Health & Inflammation',
  longevity: 'Cellular Longevity & Healthy Aging',
  gut: 'Gut & Microbiome',
  'skin-hair': 'Skin, Hair & Collagen',
};

/** Reverse lookup — canonical label → slug. */
export const GOAL_SLUG_BY_LABEL: Record<string, GoalSlug> = Object.fromEntries(
  Object.entries(GOAL_CANONICAL_LABEL).map(([slug, label]) => [label, slug as GoalSlug])
);

/** Editorial-tone descriptions for the research library — warmer than in-app copy. */
const EDITORIAL_DESCRIPTION: Record<GoalSlug, string> = {
  metabolic:
    'How your body handles sugar, fat, and energy — the quiet upstream factor behind most of what shows up on a lab report.',
  cardiovascular:
    'Heart, vessels, and circulation. The research that matters when you care more about staying alive than shaving a point off LDL.',
  'brain-cognitive':
    'Focus today and resilience decades from now — from B-vitamins to tau, what actually moves the brain and what just sounds like it does.',
  sleep:
    'The single intervention with the largest evidence base on earth, and the supplements that help (or quietly don\'t).',
  hormonal:
    'Thyroid, sex hormones, cortisol. Where small mismeasurements cost years of feeling wrong.',
  'muscle-bone-joint':
    'Strength, bone density, joint health. What the research says about aging well in a body that has to carry you.',
  immune:
    'From day-to-day resilience to the chronic inflammation that drives most aging. Evidence that separates signal from supplement marketing.',
  longevity:
    'Mitochondria, epigenetic clocks, biological age. Promising directions, honest uncertainty, and the handful of interventions with real data.',
  gut:
    'The ecosystem in your gut that quietly informs mood, immunity, and digestion. Strain-level evidence, not CFU counts.',
  'skin-hair':
    'Collagen, antioxidants, and structural proteins — what the evidence says about supporting skin, hair, and connective tissue from the inside.',
};

export interface GoalMetadata {
  slug: GoalSlug;
  label: string;
  icon: string;
  shortDescription: string;
  longDescription: string;
  editorialDescription: string;
  tier: GoalTier;
  subgoals: string[];
}

type BundleShape = {
  version: string;
  ui_metadata: Record<string, { icon: string; description: string; long_description?: string }>;
  goal_universe: {
    tiers: { TierA_default: string[]; TierB_advanced: string[] };
    subgoals: Record<string, string[]>;
  };
};

// TODO(phase-7): replace `as unknown as` with a validated load (e.g. a hand-rolled
// guard on shape at module load time) so schema drift surfaces immediately
// rather than as a runtime undefined deeper in the code.
const b = bundle as unknown as BundleShape;

function buildMetadata(slug: GoalSlug): GoalMetadata {
  const label = GOAL_CANONICAL_LABEL[slug];
  const ui = b.ui_metadata[label];
  const tier: GoalTier = b.goal_universe.tiers.TierA_default.includes(label) ? 'A' : 'B';
  return {
    slug,
    label,
    icon: ui?.icon ?? '📋',
    shortDescription: ui?.description ?? '',
    longDescription: ui?.long_description ?? ui?.description ?? '',
    editorialDescription: EDITORIAL_DESCRIPTION[slug],
    tier,
    subgoals: b.goal_universe.subgoals[label] ?? [],
  };
}

export const GOAL_METADATA: Record<GoalSlug, GoalMetadata> = Object.fromEntries(
  (Object.keys(GOAL_CANONICAL_LABEL) as GoalSlug[]).map((slug) => [slug, buildMetadata(slug)])
) as Record<GoalSlug, GoalMetadata>;

/** All 10 goals in canonical display order (Tier A first, then Tier B). */
export const GOAL_LIST: GoalMetadata[] = [
  ...b.goal_universe.tiers.TierA_default
    .map((label) => GOAL_METADATA[GOAL_SLUG_BY_LABEL[label]]),
  ...b.goal_universe.tiers.TierB_advanced
    .map((label) => GOAL_METADATA[GOAL_SLUG_BY_LABEL[label]]),
].filter(Boolean);

export const TIER_A_GOALS: GoalMetadata[] = GOAL_LIST.filter((g) => g.tier === 'A');
export const TIER_B_GOALS: GoalMetadata[] = GOAL_LIST.filter((g) => g.tier === 'B');

/** Legacy goal-ID migration (old research-library → v2.7 slugs). */
export const LEGACY_GOAL_MIGRATION: Record<string, GoalSlug> = {
  'heart-health': 'cardiovascular',
  cardiovascular: 'cardiovascular',
  'brain-function': 'brain-cognitive',
  'brain-mood': 'brain-cognitive',
  metabolic: 'metabolic',
  'metabolic-health': 'metabolic',
  sleep: 'sleep',
  'sleep-recovery': 'sleep',
  immune: 'immune',
  'immune-function': 'immune',
  inflammation: 'immune',
  hormones: 'hormonal',
  hormonal: 'hormonal',
  'hormonal-balance': 'hormonal',
  longevity: 'longevity',
  bone: 'muscle-bone-joint',
  musculoskeletal: 'muscle-bone-joint',
  'muscle-bone-joint': 'muscle-bone-joint',
  gut: 'gut',
  'gut-health': 'gut',
  skin: 'skin-hair',
  'skin-hair': 'skin-hair',
  // athletic + stress intentionally omitted — they become `topics[]` entries,
  // not goal tags. See mappers for the split logic.
};

/** Get display metadata for a goal slug, tolerant of legacy IDs. */
export function getGoalMetadata(slugOrLegacy: string): GoalMetadata | undefined {
  if (slugOrLegacy in GOAL_METADATA) return GOAL_METADATA[slugOrLegacy as GoalSlug];
  const migrated = LEGACY_GOAL_MIGRATION[slugOrLegacy];
  return migrated ? GOAL_METADATA[migrated] : undefined;
}

/** Migrate a legacy goal id array to canonical v2.7 slugs, deduplicating. */
export function migrateGoalIds(legacy: string[]): GoalSlug[] {
  const seen = new Set<GoalSlug>();
  const out: GoalSlug[] = [];
  for (const id of legacy) {
    const slug = LEGACY_GOAL_MIGRATION[id] ?? (id in GOAL_METADATA ? (id as GoalSlug) : undefined);
    if (slug && !seen.has(slug)) {
      seen.add(slug);
      out.push(slug);
    }
  }
  return out;
}
